import { useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import useAuth from '../../hooks/useAuth';
import UserIcon from '../../icons/User';
import getInitials from '../../utils/getInitials';
import useSettings from '../../hooks/useSettings';
import DevRolePanel from './DevRolePanel';
import useDevRole from '../../hooks/useDevRole';

const getValues = (settings) => ({
  compact: settings.compact,
  direction: settings.direction,
  responsiveFontSizes: settings.responsiveFontSizes,
  roundedCorners: settings.roundedCorners,
  openDrawer: settings.openDrawer,
  theme: settings.theme,
});

function AccountPopover() {
  const anchorRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { settings, saveSettings } = useSettings();
  const [values, setValues] = useState(getValues(settings));

  // Dev role emulation state (only active in development)
  const { isEmulatingRoles, isClearingSucursales } = useDevRole();

  useEffect(() => {
    setValues(getValues(settings));
  }, [settings]);

  const handleOpenDrawer = () => {
    saveSettings({ ...values, openDrawer: true });
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('No fue posible salir.');
    }
  };

  return (
    <>
      <Box component={ButtonBase} onClick={handleOpen} ref={anchorRef} sx={{ alignItems: 'center', display: 'flex' }}>
        <Avatar
          src={user.avatar}
          sx={(theme) => ({
            height: 32,
            width: 32,
            ...((isEmulatingRoles || isClearingSucursales) && {
              border: `2px solid ${theme.palette.warning.light}`,
              boxShadow: `0 0 4px ${theme.palette.warning.light}`,
            }),
          })}
        >
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
            {getInitials(user.name)}
          </Typography>
        </Avatar>
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: { width: 240 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography color="textPrimary" variant="subtitle2">
            {user.name}
          </Typography>
          <Typography color="textSecondary" variant="subtitle2">
            {user.profile}
          </Typography>
        </Box>
        <Divider />
        {import.meta.env.MODE === 'development' && (
          <>
            <DevRolePanel />
            <Divider />
          </>
        )}
        <Box sx={{ mt: 2 }}>
          <MenuItem onClick={handleClose} component={RouterLink} to="/panel/perfil">
            <ListItemIcon>
              <UserIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="textPrimary" variant="subtitle2">
                  Mi Perfil
                </Typography>
              }
            />
          </MenuItem>
          <MenuItem onClick={handleOpenDrawer}>
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography color="textPrimary" variant="subtitle2">
                  Opciones
                </Typography>
              }
            />
          </MenuItem>
        </Box>
        <Box sx={{ p: 2 }}>
          <Button color="primary2" fullWidth onClick={handleLogout} variant="outlined">
            Salir
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default AccountPopover;
